import { OfficialRateModel, OfficialRateRuleModel } from "@/components/property/official-rates/types";
import { isArray } from "lodash";
import { PriceChange } from "../calendar/inputs/PriceInput.vue";
import { AccomodationUnitTypeViewModel } from "@/shared/models/accomodationUnitTypeViewModel";

/**
 * Gets the given culture's thousands and decimal separators
 * @param {string} locale - [optional] the locale that the number is represented in. Omit this parameter to use the current locale.
 */
export function getSeparators(locale?: string){
  const thousandSeparator = Intl.NumberFormat(locale)
    .format(11111)
    .replace(/\p{Number}/gu, "");
  const decimalSeparator = Intl.NumberFormat(locale)
    .format(1.1)
    .replace(/\p{Number}/gu, "");

  return {
    thousands: thousandSeparator,
    decimals: decimalSeparator
  }
}

/**
 * Parse a localized number to a float.
 * @param {string?} stringNumber - the localized number
 * @param {string} locale - [optional] the locale that the number is represented in. Omit this parameter to use the current locale.
 */
export function parseLocaleNumber(
  stringNumber: string | null | undefined,
  locale?: string
): number {
  // removing whitespaces from input
  if (stringNumber) { 
    stringNumber = stringNumber.replaceAll(" ", "");
  }

  if (
    !stringNumber ||
    stringNumber.trim() === "" ||
    stringNumber.trim() === "-"|| // dash
    stringNumber.trim() === "−"   // Unicode Character “−” (U+2212)
  )
    return 0;

  const separators = getSeparators(locale);

  const result = parseFloat(
    stringNumber
      .replace(new RegExp("−"), "-") // this is not the same, it replaces 'minus' wth 'dash'
      .replace(new RegExp("\\" + separators.thousands, "g"), "")
      .replace(new RegExp("\\" + separators.decimals), ".")
  );

  return isNaN(result) ? 0 : result;
}

export function formatLocaleNumber(
  number: number | null | undefined,
  decimals: number | [number,number] | undefined,
  locale?: string
): string {
  const [min, max] = isArray(decimals)
    ? decimals
    : [decimals, decimals]

  if (number === 0 || number) {
    return Intl.NumberFormat(locale, {
      minimumFractionDigits: min,
      maximumFractionDigits: max,
    })
    .format(number)
    .replace(new RegExp("−"), "-"); // this is not the same, it replaces 'minus' wth 'dash'
    // i'm handling it in parse function, but ... i officially hate this character now
  } else {
    return "";
  }
}

export function round(value: number, decimals = 2){
  let internalValue = value;
  const isNegative = value < 0;

  // determine the number by which to multiply and later divide the value in order to get the correct number of decimal places
  const decimalCorrector = 10**decimals;

  // JS rounds UP instead of away-from-zero, so we need to flip negative numbers
  // Math.round(-1.5) = -1
  if (isNegative) internalValue *= -1;

  // round the value
  internalValue *= decimalCorrector;
  internalValue = Math.round(internalValue + (Math.round(internalValue) * Number.EPSILON));
  internalValue /= decimalCorrector;

  // end then flip them back when done
  if (isNegative) internalValue *= -1;
  
  return internalValue;
}

export function autoCalculatePrice(
  isInputPriceChanged: boolean,
  officialRateInternal: OfficialRateModel | undefined,
  ownerPayoutPercent: number,
  index?: number,
  uniqueKey?: string
): void {
  if (!officialRateInternal) return;

  const rule = getPriceRule(officialRateInternal, index, uniqueKey);

  if (rule) {
    const price = isInputPriceChanged ? rule.inputPrice || 0 : rule.price || 0;
    const newPrice = calculateNewPrice(price, ownerPayoutPercent, isInputPriceChanged);

    if (isInputPriceChanged) {
      rule.price = newPrice;
    } else {
      rule.inputPrice = newPrice;
    }
  }
}

// Helper function to calculate the new price
function calculateNewPrice(
  price: number,
  ownerPayoutPercent: number,
  isInputPriceChanged: boolean
): number {
  const priceToReturn = isInputPriceChanged
    ? price / (ownerPayoutPercent / 100)
    : price * (ownerPayoutPercent / 100);

    return round(priceToReturn);
}

// Helper function to get the correct price rule based on index or uniqueKey
function getPriceRule(
  officialRateInternal: OfficialRateModel,
  index?: number,
  uniqueKey?: string
): OfficialRateRuleModel | OfficialRateModel | undefined {
  if (index !== undefined) {
    return officialRateInternal.officialRateRules[index];
  } else if (uniqueKey) {
    return officialRateInternal.officialRateRules.find(
      (rule) => rule.uniqueKey === uniqueKey
    );
  } else {
    return officialRateInternal; // For the main price
  }
}

export function autoCalculateAbsolutePrice(
  priceChange: PriceChange,
  inputPriceChanged: boolean,
  obr: OfficialRateRuleModel,
  officialRateInternal: OfficialRateModel | undefined
) {
  const priceReference = inputPriceChanged 
    ? (obr.price ?? 0) 
    : (obr.inputPrice ?? 0);

  const amount = getRecalculatedAmount(priceChange, priceReference);

  if (officialRateInternal) {
      inputPriceChanged 
        ? obr.inputPrice = !amount || (amount && amount >= 0) ? amount : 0 //if null -> save null, if negative -> save zero
        : obr.price = !amount || (amount && amount >= 0) ? amount : 0; //if null -> save null, if negative -> save zero
  }
}

export function autoCalculatePriceWhenRelative(
  isInputPriceChanged: boolean, 
  obr: OfficialRateRuleModel,
  accUnitType: AccomodationUnitTypeViewModel | undefined
) {
  const ownerPayoutPercent = accUnitType?.propertyOwnerPayoutPercent ?? 0;

  if (isInputPriceChanged) {
    obr.price = round((obr.inputPrice || 0) / (ownerPayoutPercent / 100));
  } else {
    obr.inputPrice = round((obr.price || 0) * (ownerPayoutPercent / 100));
  }
}

export function getRecalculatedAmount(priceChange: PriceChange, priceReference?: number) : number{
  let amount = !priceChange.percentage
    ? priceChange.value
    : round(((priceReference ?? 0) * (priceChange.value ?? 0)) / 100);

  if (!priceReference && priceChange.subtraction) {
    amount = 0;
  }

  if (priceChange.addition) {
    amount = (priceReference || priceReference == 0) && amount 
      ? priceReference + amount 
      : null;
  } 
  else if (priceChange.subtraction) {
    amount = (priceReference || priceReference == 0) && amount 
      ? priceReference - amount 
      : null;
  }

  return round(amount ?? 0);
}

export function calculatePrice(
  priceChange: PriceChange,
  officialRate: OfficialRateModel | undefined,
  userChangedInputPrice: boolean,
  priceReference?: number,
  options?: {
    isMain?: boolean;
    index?: number;
    uniqueKey?: string;
  }
): boolean {
  if (!priceChange) return false ;

  let changeKey = false;
  
  const amount = getRecalculatedAmount(priceChange, priceReference)

  if (userChangedInputPrice) {
    if (options?.isMain && officialRate) {
      const oldValue = officialRate.inputPrice;
      officialRate.inputPrice = amount ?? 0;

      if (oldValue === officialRate.inputPrice) changeKey = true;
    } 
    else if (options?.index != null && officialRate?.officialRateRules) {
      const oldValue = officialRate.officialRateRules[options.index].inputPrice;
      officialRate.officialRateRules[options.index].inputPrice = amount != null && amount >= 0 ? amount : 0;
      
      if (oldValue === officialRate.officialRateRules[options.index].inputPrice) changeKey = true;
    } 
    else if (options?.uniqueKey && officialRate?.officialRateRules) {
      const rule = officialRate.officialRateRules.find((r) => r.uniqueKey === options.uniqueKey);
      
      if (rule) {
        const oldValue = rule.inputPrice;
        rule.inputPrice = amount != null && amount >= 0 ? amount : 0;
        
        if (oldValue === rule.inputPrice) changeKey = true;
      }
    }
    else {
      if (userChangedInputPrice && officialRate) {
        officialRate.inputPrice = amount;
        changeKey = true;
      }
    }
  } 
  else {
    if (options?.isMain && officialRate) {
      const oldValue = officialRate.price;
      officialRate.price = amount != null ? amount : 0;
      
      if (oldValue === officialRate.price) changeKey = true;
    } 
    else if (options?.index != null && officialRate?.officialRateRules) {
      const oldValue = officialRate.officialRateRules[options.index].price;
      officialRate.officialRateRules[options.index].price = amount != null && amount >= 0 ? amount : 0;
      
      if (oldValue === officialRate.officialRateRules[options.index].price) changeKey = true;
    } 
    else if (options?.uniqueKey && officialRate?.officialRateRules) {
      const rule = officialRate.officialRateRules.find((r) => r.uniqueKey === options.uniqueKey);
      
      if (rule) {
        const oldValue = rule.price;
        rule.price = amount != null && amount >= 0 ? amount : 0;
        
        if (oldValue === rule.price) changeKey = true;
      }
    }
    else {
      if (officialRate) {
        officialRate.price = amount;
        changeKey = true;
      }
    }
  }

  return changeKey;
}
